<script setup lang="ts">
/**
 * The composer's jurisdiction picker (MAR-1418): which ONE country's law this conversation is about.
 *
 * Rendered only when the organization has more than one jurisdiction to offer — a single-jurisdiction
 * org sees exactly the composer it had before (AC2, AC5). It defaults to the org's main; the pick is
 * saved on the conversation by the store (`useChatJurisdiction`), and a new chat always starts on main
 * again. Sits beside the model and QRA selectors and borrows their quiet native-`<select>` shape.
 */
import { ChevronDown, Landmark } from 'lucide-vue-next'
import { showsJurisdictionPicker } from '~~/shared/chat/jurisdiction'
import { useJurisdictionName } from '~/composables/useJurisdictionName'

const { t } = useI18n()
const webChat = useWebChatStore()
const jurisdictionName = useJurisdictionName()

const visible = computed(() => showsJurisdictionPicker(webChat.jurisdictionOptions))
const label = computed(() => jurisdictionName(webChat.activeJurisdiction))

function onChange(e: Event) {
  void webChat.setJurisdiction((e.target as HTMLSelectElement).value)
}

onMounted(() => { void webChat.ensureJurisdictionOptions() })
</script>

<template>
  <div
    v-if="visible"
    data-testid="chat-jurisdiction-picker"
    class="group relative inline-flex items-center gap-1"
    :title="t('chat.chatJurisdiction')"
  >
    <Landmark class="pointer-events-none h-3 w-3 text-muted-foreground" />
    <select
      :value="webChat.activeJurisdiction"
      class="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
      :aria-label="t('chat.chatJurisdiction')"
      @change="onChange"
    >
      <option v-for="code in webChat.jurisdictionOptions" :key="code" :value="code">
        {{ jurisdictionName(code) }}
      </option>
    </select>
    <span class="pointer-events-none select-none text-xs text-muted-foreground group-hover:text-foreground">{{ label }}</span>
    <ChevronDown class="pointer-events-none h-3 w-3 text-muted-foreground group-hover:text-foreground" />
  </div>
  <!-- A refused save was rolled back by the store; say so rather than let the pill snap back silently.
       Outside the pill, so the invisible select overlaying it does not swallow the message. -->
  <span v-if="visible && webChat.jurisdictionError" role="alert" class="text-xs text-destructive">{{ t('chat.chatJurisdictionFailed') }}</span>
</template>
